import { Component } from '../Component';

/**
 * MovementComponent - Movement state and physics
 */
export class MovementComponent extends Component {
  public readonly type = 'movement';

  /** Current velocity X */
  public velocityX: number;

  /** Current velocity Y */
  public velocityY: number;

  /** Maximum movement speed */
  public maxSpeed: number;

  /** Acceleration rate */
  public acceleration: number;

  /** Deceleration/friction rate */
  public friction: number;

  /** Target position for pathfinding */
  public targetX: number | null;
  public targetY: number | null;

  /** Current path (array of waypoints) */
  public path: { x: number; y: number }[];

  /** Current path index */
  public pathIndex: number;

  /** Whether entity can move */
  public canMove: boolean;

  /** Whether entity is currently moving */
  public isMoving: boolean;

  /** Movement speed modifier (for buffs/debuffs) */
  public speedModifier: number;

  constructor(data?: Partial<MovementComponent>) {
    super();
    this.velocityX = data?.velocityX ?? 0;
    this.velocityY = data?.velocityY ?? 0;
    this.maxSpeed = data?.maxSpeed ?? 100;
    this.acceleration = data?.acceleration ?? 500;
    this.friction = data?.friction ?? 400;
    this.targetX = data?.targetX ?? null;
    this.targetY = data?.targetY ?? null;
    this.path = data?.path ?? [];
    this.pathIndex = data?.pathIndex ?? 0;
    this.canMove = data?.canMove ?? true;
    this.isMoving = data?.isMoving ?? false;
    this.speedModifier = data?.speedModifier ?? 1;
  }

  /**
   * Get effective max speed with modifiers
   */
  public get effectiveMaxSpeed(): number {
    return this.maxSpeed * this.speedModifier;
  }

  /**
   * Set movement target
   */
  public setTarget(x: number, y: number): void {
    this.targetX = x;
    this.targetY = y;
  }

  /**
   * Clear movement target
   */
  public clearTarget(): void {
    this.targetX = null;
    this.targetY = null;
  }

  /**
   * Set path for pathfinding
   */
  public setPath(path: { x: number; y: number }[]): void {
    this.path = path;
    this.pathIndex = 0;
  }

  /**
   * Clear current path
   */
  public clearPath(): void {
    this.path = [];
    this.pathIndex = 0;
  }

  /**
   * Get current path waypoint
   */
  public getCurrentWaypoint(): { x: number; y: number } | null {
    if (this.pathIndex >= this.path.length) return null;
    return this.path[this.pathIndex];
  }

  /**
   * Advance to next waypoint
   */
  public advanceWaypoint(): void {
    this.pathIndex++;
  }

  /**
   * Check if path is complete
   */
  public isPathComplete(): boolean {
    return this.pathIndex >= this.path.length;
  }

  /**
   * Stop all movement
   */
  public stop(): void {
    this.velocityX = 0;
    this.velocityY = 0;
    this.isMoving = false;
    this.clearTarget();
    this.clearPath();
  }

  /**
   * Apply velocity based on direction
   */
  public moveInDirection(dirX: number, dirY: number, delta: number): void {
    // Normalize direction
    const length = Math.sqrt(dirX * dirX + dirY * dirY);
    if (length > 0) {
      const normalX = dirX / length;
      const normalY = dirY / length;

      // Apply acceleration
      this.velocityX += normalX * this.acceleration * (delta / 1000);
      this.velocityY += normalY * this.acceleration * (delta / 1000);

      // Clamp to max speed
      const currentSpeed = Math.sqrt(
        this.velocityX * this.velocityX + this.velocityY * this.velocityY
      );
      if (currentSpeed > this.effectiveMaxSpeed) {
        const scale = this.effectiveMaxSpeed / currentSpeed;
        this.velocityX *= scale;
        this.velocityY *= scale;
      }

      this.isMoving = true;
    }
  }

  /**
   * Apply friction to slow down
   */
  public applyFriction(delta: number): void {
    const frictionAmount = this.friction * (delta / 1000);
    const currentSpeed = Math.sqrt(
      this.velocityX * this.velocityX + this.velocityY * this.velocityY
    );

    if (currentSpeed > frictionAmount) {
      const scale = (currentSpeed - frictionAmount) / currentSpeed;
      this.velocityX *= scale;
      this.velocityY *= scale;
    } else {
      this.velocityX = 0;
      this.velocityY = 0;
      this.isMoving = false;
    }
  }
}
